import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { ApiError } from '@/shared/types/api.types';
import type { ModelId, ProjectId, ProjectMemberId } from '@/shared/types/common.types';
import { apiClient } from '@/shared/utils/api/apiClient';
import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import { NO_CAPABILITIES, capabilitiesSchema, toCapabilities } from '../api/capabilityContracts';
import {
  projectHealthSchema,
  projectSummarySchema,
  toProjectHealth,
  toProjectSummary,
} from '../api/rollupContracts';
import {
  memberPageSchema,
  memberSchema,
  projectListSchema,
  projectSchema,
  tenantPageSchema,
  toCursorPage,
  toMember,
  toProject,
  toProjectListPage,
  toTenant,
} from '../api/workspaceContracts';
import type { ProjectHealth, ProjectSummary } from '../types/rollup.types';
import type {
  AddMemberInput,
  Capabilities,
  CreateProjectInput,
  CursorPage,
  ProjectListPage,
  ProjectListQuery,
  ProjectMember,
  Tenant,
  ModelStatus,
  UpdateMemberInput,
  UpdateProjectInput,
  WorkspaceProject,
} from '../types/workspace.types';

export const workspaceService = {
  /**
   * A `404` means this Core predates the route and so supports none of the v1 features.
   * Any other failure is thrown, so React Query can retry it; until it succeeds the caller
   * treats every feature as off.
   */
  async getCapabilities(): Promise<Capabilities> {
    let payload: unknown;
    try {
      payload = await apiClient.get<unknown>(API_ENDPOINTS.capabilities);
    } catch (error) {
      if (error instanceof ApiError && error.kind === 'notFound') return NO_CAPABILITIES;
      throw error;
    }
    return toCapabilities(parseOrThrow(capabilitiesSchema, payload, 'capabilities response'));
  },

  async listTenants(): Promise<CursorPage<Tenant>> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.tenants);
    return toCursorPage(parseOrThrow(tenantPageSchema, payload, 'tenant list'), toTenant);
  },

  async listProjects(query: ProjectListQuery = {}): Promise<ProjectListPage> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.projects, {
      params: {
        q: query.q === undefined || query.q.length === 0 ? undefined : query.q,
        cursor: query.cursor,
        limit: query.limit,
      },
    });
    return toProjectListPage(parseOrThrow(projectListSchema, payload, 'project list'));
  },

  /**
   * `idempotencyKey` is generated once per create attempt, so a retried request that Core
   * already handled returns the same project instead of a duplicate.
   */
  async createProject(input: CreateProjectInput, idempotencyKey: string): Promise<WorkspaceProject> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.workspace.projects, input, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return toProject(parseOrThrow(projectSchema, payload, 'created project'));
  },

  async getProject(projectId: ProjectId): Promise<WorkspaceProject> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.project(projectId));
    return toProject(parseOrThrow(projectSchema, payload, 'project'));
  },

  /** `etag` comes from the project as last read; Core answers `412` if it has changed since. */
  async updateProject(
    projectId: ProjectId,
    input: UpdateProjectInput,
    etag: string
  ): Promise<WorkspaceProject> {
    const payload = await apiClient.patch<unknown>(API_ENDPOINTS.workspace.project(projectId), input, {
      headers: { 'If-Match': etag },
    });
    return toProject(parseOrThrow(projectSchema, payload, 'updated project'));
  },

  async deleteProject(projectId: ProjectId, etag: string): Promise<void> {
    await apiClient.delete<unknown>(API_ENDPOINTS.workspace.project(projectId), {
      headers: { 'If-Match': etag },
    });
  },

  async getSummary(projectId: ProjectId): Promise<ProjectSummary> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.summary(projectId));
    return toProjectSummary(parseOrThrow(projectSummarySchema, payload, 'project summary'));
  },

  async getHealth(projectId: ProjectId): Promise<ProjectHealth> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.health(projectId));
    return toProjectHealth(parseOrThrow(projectHealthSchema, payload, 'project health'));
  },

  /** Core answers `204` and does nothing when the model is already linked. */
  async linkModel(projectId: ProjectId, modelId: ModelId): Promise<void> {
    await apiClient.post<unknown>(API_ENDPOINTS.workspace.models(projectId), { modelId: Number(modelId) });
  },

  /** Sets a linked model's status within the project (proposed route; project admins only). */
  async updateModelStatus(
    projectId: ProjectId,
    modelId: ModelId,
    status: Exclude<ModelStatus, 'Unknown'>
  ): Promise<void> {
    await apiClient.patch<unknown>(
      `${API_ENDPOINTS.workspace.models(projectId)}/${encodeURIComponent(String(modelId))}`,
      { status }
    );
  },

  /** Sets the project member answerable for a linked model, or clears it with null (proposed route). */
  async updateModelResponsible(
    projectId: ProjectId,
    modelId: ModelId,
    responsible: string | null
  ): Promise<void> {
    await apiClient.patch<unknown>(
      `${API_ENDPOINTS.workspace.models(projectId)}/${encodeURIComponent(String(modelId))}`,
      { responsible }
    );
  },

  async unlinkModel(projectId: ProjectId, modelId: ModelId): Promise<void> {
    await apiClient.delete<unknown>(
      `${API_ENDPOINTS.workspace.models(projectId)}/${encodeURIComponent(String(modelId))}`
    );
  },

  async listMembers(projectId: ProjectId, cursor?: string): Promise<CursorPage<ProjectMember>> {
    const payload = await apiClient.get<unknown>(API_ENDPOINTS.workspace.members(projectId), {
      params: { cursor },
    });
    return toCursorPage(parseOrThrow(memberPageSchema, payload, 'project member list'), toMember);
  },

  async addMember(
    projectId: ProjectId,
    input: AddMemberInput,
    idempotencyKey: string
  ): Promise<ProjectMember> {
    const payload = await apiClient.post<unknown>(API_ENDPOINTS.workspace.members(projectId), input, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    return toMember(parseOrThrow(memberSchema, payload, 'project member'));
  },

  async updateMember(
    projectId: ProjectId,
    memberId: ProjectMemberId,
    input: UpdateMemberInput
  ): Promise<ProjectMember> {
    const payload = await apiClient.patch<unknown>(
      API_ENDPOINTS.workspace.member(projectId, memberId),
      input
    );
    return toMember(parseOrThrow(memberSchema, payload, 'project member'));
  },

  async removeMember(projectId: ProjectId, memberId: ProjectMemberId): Promise<void> {
    await apiClient.delete<unknown>(API_ENDPOINTS.workspace.member(projectId, memberId));
  },
};
